import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Volume2, VolumeX, Eye, EyeOff, Check, X, Globe, Home, Play, Pause, AlertCircle } from 'lucide-react';

// Colors for training - including black and white
const COLORS = [
  { id: 'yellow', nameTr: 'Sarı', nameEn: 'Yellow', hex: '#FBBF24' },
  { id: 'red', nameTr: 'Kırmızı', nameEn: 'Red', hex: '#EF4444' },
  { id: 'blue', nameTr: 'Mavi', nameEn: 'Blue', hex: '#3B82F6' },
  { id: 'green', nameTr: 'Yeşil', nameEn: 'Green', hex: '#22C55E' },
  { id: 'purple', nameTr: 'Mor', nameEn: 'Purple', hex: '#A855F7' },
  { id: 'orange', nameTr: 'Turuncu', nameEn: 'Orange', hex: '#F97316' },
  { id: 'black', nameTr: 'Siyah', nameEn: 'Black', hex: '#1a1a1a' },
  { id: 'white', nameTr: 'Beyaz', nameEn: 'White', hex: '#f5f5f5' }
];

// Get random color
const getRandomColor = () => {
  return COLORS[Math.floor(Math.random() * COLORS.length)];
};

// Text-to-Speech helper with female voice preference
const speak = (text, lang = 'tr-TR') => {
  return new Promise((resolve) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.9;
    utterance.pitch = 1.2;
    
    const voices = window.speechSynthesis.getVoices();
    const femaleVoice = voices.find(v => 
      v.lang.startsWith(lang.split('-')[0]) && 
      (v.name.toLowerCase().includes('female') || 
       v.name.toLowerCase().includes('kadın') ||
       v.name.toLowerCase().includes('woman'))
    );
    if (femaleVoice) {
      utterance.voice = femaleVoice;
    }
    
    utterance.onend = resolve;
    utterance.onerror = resolve;
    window.speechSynthesis.speak(utterance);
  });
};

// Language Selector
const LanguageSelector = ({ language, onSelect }) => (
  <div className="flex items-center gap-2">
    <Globe className="w-4 h-4 text-slate-400" />
    <div className="flex rounded-full overflow-hidden border border-slate-700">
      <button
        onClick={() => onSelect('tr')}
        className={`px-3 py-1 text-xs transition-colors ${
          language === 'tr' ? 'bg-violet-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
        }`}
      >
        TR
      </button>
      <button
        onClick={() => onSelect('en')}
        className={`px-3 py-1 text-xs transition-colors ${
          language === 'en' ? 'bg-violet-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
        }`}
      >
        EN
      </button>
    </div>
  </div>
);

// Home Button Component
const HomeButton = ({ onHome }) => (
  <button
    onClick={onHome}
    className="fixed top-6 right-6 z-50 glass-panel p-3 text-slate-400 hover:text-white transition-all flex items-center gap-2"
    data-testid="home-button"
  >
    <Home className="w-5 h-5" />
    <span className="text-sm">Home</span>
  </button>
);

// Instructions Screen - Shows before test starts
const InstructionsScreen = ({ language, onContinue, onBack, onHome }) => {
  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      <HomeButton onHome={onHome} />
      
      <div className="max-w-2xl w-full space-y-8 text-center">
        <motion.div
          className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center border border-violet-500/30"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <EyeOff className="w-12 h-12 text-violet-400" />
        </motion.div>
        
        <h1 className="text-3xl font-heading text-slate-100">
          {language === 'tr' ? 'Teste Hazırlan' : 'Get Ready for Test'}
        </h1>
        
        {/* Important Notice */}
        <div className="glass-panel p-6 space-y-4 text-left border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center gap-3 text-amber-400">
            <AlertCircle className="w-6 h-6 flex-shrink-0" />
            <h3 className="text-lg font-medium">
              {language === 'tr' ? 'Önemli Bilgilendirme' : 'Important Notice'}
            </h3>
          </div>
          
          <div className="space-y-4 text-slate-300">
            <p>
              {language === 'tr' 
                ? '🎭 Şimdi gözlerini ışık geçirmeyen koyu renkli bir göz bandı ile kapat.'
                : '🎭 Now close your eyes with a dark, light-blocking eye mask.'}
            </p>
            <p>
              {language === 'tr'
                ? '🎨 Sana renkleri göstereceğiz ve ekrana yansıtacağız. Toplamda 5 farklı rengi rastgele seçeceğiz.'
                : '🎨 We will show you colors on the screen. We will randomly select 5 different colors.'}
            </p>
            <p>
              {language === 'tr'
                ? '⏱️ Her renk 15 saniye ekranda kalacak ve sürenin sonunda sesli şekilde duyacaksın.'
                : '⏱️ Each color will stay on screen for 15 seconds and you will hear it announced at the end.'}
            </p>
            <p className="text-amber-400 font-medium">
              {language === 'tr'
                ? '⚠️ Biz "Test bitti, gözlerini açabilirsin" diyene kadar gözlerini açma!'
                : '⚠️ Do not open your eyes until we say "Test is over, you can open your eyes"!'}
            </p>
          </div>
        </div>
        
        {/* Blindfold Note */}
        <div className="glass-panel p-4 text-sm text-slate-400 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
            🕶️
          </div>
          <p className="text-left">
            {language === 'tr'
              ? 'Renk testinde ışık geçirmeyen koyu renkli bir göz bandı kullanılmalıdır. Işık sızması test sonuçlarını etkileyebilir.'
              : 'A dark, light-blocking blindfold should be used for the color test. Light leakage may affect test results.'}
          </p>
        </div>
        
        <button
          onClick={onContinue}
          className="w-full max-w-sm mx-auto rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 px-8 py-4 text-sm font-medium text-white transition-all hover:from-violet-500/30 hover:to-cyan-500/30 border border-violet-500/30 flex items-center justify-center gap-2"
        >
          <Play className="w-4 h-4" />
          {language === 'tr' ? 'Göz Bandımı Taktım, Başla' : 'I Put On My Blindfold, Start'}
        </button>
      </div>
    </motion.div>
  );
};

// Intro Screen
const IntroScreen = ({ language, onLanguageChange, onStart, onBack, onHome }) => {
  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      <HomeButton onHome={onHome} />
      
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-40">
        <LanguageSelector language={language} onSelect={onLanguageChange} />
      </div>
      
      <div className="max-w-2xl w-full space-y-8 text-center">
        <div className="space-y-4">
          <motion.div
            className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center border border-violet-500/30"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Eye className="w-12 h-12 text-violet-400" />
          </motion.div>
          
          <h1 className="text-3xl font-heading text-slate-100">
            {language === 'tr' ? 'Renk Algılama Eğitimi' : 'Color Perception Training'}
          </h1>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            {language === 'tr' 
              ? 'Gözlerinizi kapatın ve renkleri zihninizle görmeye çalışın.'
              : 'Close your eyes and try to see colors with your mind.'}
          </p>
        </div>
        
        {/* Color Preview */}
        <div className="flex flex-wrap justify-center gap-3">
          {COLORS.map((color) => (
            <div
              key={color.id}
              className="w-12 h-12 rounded-xl border border-white/10 shadow-lg"
              style={{ backgroundColor: color.hex }}
              title={language === 'tr' ? color.nameTr : color.nameEn}
            />
          ))}
        </div>
        
        {/* How it works */}
        <div className="glass-panel p-6 space-y-4 text-left">
          <h3 className="text-cyan-400 text-sm font-medium">
            {language === 'tr' ? 'Nasıl Çalışır?' : 'How It Works?'}
          </h3>
          <ol className="space-y-3 text-sm text-slate-300">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-xs flex-shrink-0">1</span>
              <span>{language === 'tr' ? 'Koyu renkli göz bandı ile gözlerinizi kapatın' : 'Close your eyes with a dark blindfold'}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-xs flex-shrink-0">2</span>
              <span>{language === 'tr' ? '5 farklı renk ekrana yansıtılacak, her biri 15 saniye' : '5 different colors will be displayed, each for 15 seconds'}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-xs flex-shrink-0">3</span>
              <span>{language === 'tr' ? 'Süre dolduğunda renk sesli olarak söylenecek' : 'When time is up, the color will be announced'}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-xs flex-shrink-0">4</span>
              <span>{language === 'tr' ? 'Test bitince kaç tane doğru bildiğinizi işaretleyeceksiniz' : 'After the test, you will mark how many you got right'}</span>
            </li>
          </ol>
        </div>
        
        <button
          onClick={onStart}
          className="w-full max-w-sm mx-auto rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 px-8 py-4 text-sm font-medium text-white transition-all hover:from-violet-500/30 hover:to-cyan-500/30 border border-violet-500/30 flex items-center justify-center gap-2"
        >
          <Play className="w-4 h-4" />
          {language === 'tr' ? 'Eğitime Başla' : 'Start Training'}
        </button>
      </div>
    </motion.div>
  );
};

// Test Phase - Full screen color with countdown
const TestPhase = ({ language, round, totalRounds, shownColors, onComplete, onExit, onHome }) => {
  const [currentColor, setCurrentColor] = useState(null);
  const [countdown, setCountdown] = useState(15);
  const [showColor, setShowColor] = useState(true);
  const [isRevealing, setIsRevealing] = useState(false);
  const [phase, setPhase] = useState('focus'); // focus, reveal
  
  const langCode = language === 'tr' ? 'tr-TR' : 'en-US';
  
  // Initialize random color
  useEffect(() => {
    const newColor = getRandomColor();
    setCurrentColor(newColor);
    setCountdown(15);
    setShowColor(true);
    setIsRevealing(false);
    setPhase('focus');
  }, [round]);
  
  // Countdown timer
  useEffect(() => {
    if (phase !== 'focus' || !currentColor) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          revealColor();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [phase, currentColor]);
  
  // Reveal color and speak
  const revealColor = async () => {
    setIsRevealing(true);
    setPhase('reveal');
    
    const colorName = language === 'tr' ? currentColor.nameTr : currentColor.nameEn;
    await speak(colorName, langCode);
    
    // Wait then go to next
    setTimeout(() => {
      setIsRevealing(false);
      handleNext();
    }, 2000);
  };
  
  // Next round or complete
  const handleNext = () => {
    const newShownColors = [...shownColors, currentColor];
    if (round >= totalRounds) {
      // Test complete - announce it
      speak(language === 'tr' ? 'Test bitti. Gözlerini açabilirsin.' : 'Test is over. You can open your eyes.', langCode);
      onComplete(newShownColors);
    } else {
      onComplete(null, round + 1, newShownColors);
    }
  };
  
  if (!currentColor) return null;
  
  const textColor = currentColor.id === 'white' || currentColor.id === 'yellow' ? '#1a1a1a' : '#ffffff';
  
  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center"
      style={{ backgroundColor: currentColor.hex }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Exit Button */}
      <button
        onClick={onExit}
        className="fixed top-6 left-6 z-50 p-3 rounded-xl bg-black/20 text-white/60 hover:text-white transition-all"
      >
        <X className="w-5 h-5" />
      </button>
      
      {/* Round indicator */}
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50">
        <div className="px-4 py-2 rounded-xl bg-black/20 text-sm" style={{ color: textColor }}>
          {language === 'tr' ? 'Renk' : 'Color'} {round} / {totalRounds}
        </div>
      </div>
      
      {/* Home Button */}
      <button
        onClick={onHome}
        className="fixed top-6 right-6 z-50 p-3 rounded-xl bg-black/20 text-white/60 hover:text-white transition-all flex items-center gap-2"
      >
        <Home className="w-5 h-5" />
        <span className="text-sm">Home</span>
      </button>
      
      {/* Center content - for screen reader users or debugging only */}
      <div className="text-center space-y-8 opacity-0">
        <div className="text-6xl font-heading" style={{ color: textColor }}>
          {countdown}
        </div>
      </div>
      
      {/* Reveal text */}
      {phase === 'reveal' && (
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <div className="text-center">
            <Volume2 className="w-16 h-16 mx-auto mb-4" style={{ color: textColor }} />
            <h2 className="text-6xl font-heading" style={{ color: textColor }}>
              {language === 'tr' ? currentColor.nameTr : currentColor.nameEn}
            </h2>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// Score Selection Screen
const ScoreSelectionScreen = ({ language, totalRounds, shownColors, onSubmit, onBack, onHome }) => {
  const [selectedScore, setSelectedScore] = useState(null);
  
  const handleSubmit = () => {
    if (selectedScore !== null) {
      onSubmit(selectedScore);
    }
  };
  
  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      <HomeButton onHome={onHome} />
      
      <div className="max-w-2xl w-full space-y-8 text-center">
        <div className="space-y-4">
          <motion.div
            className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-green-500/20 to-cyan-500/20 flex items-center justify-center border border-green-500/30"
          >
            <Check className="w-10 h-10 text-green-400" />
          </motion.div>
          
          <h1 className="text-3xl font-heading text-slate-100">
            {language === 'tr' ? 'Test Tamamlandı!' : 'Test Complete!'}
          </h1>
          <p className="text-slate-400">
            {language === 'tr' ? 'Gözlerini açabilirsin.' : 'You can open your eyes now.'}
          </p>
        </div>
        
        {/* Shown colors */}
        <div className="glass-panel p-6 space-y-4">
          <h3 className="text-cyan-400 text-sm font-medium text-left">
            {language === 'tr' ? 'Gösterilen Renkler' : 'Shown Colors'}
          </h3>
          <div className="flex flex-wrap gap-3 justify-center">
            {shownColors.map((color, index) => (
              <div key={`shown-${color.id}-${index}`} className="flex flex-col items-center gap-2">
                <div
                  className="w-14 h-14 rounded-xl border border-white/10 shadow-lg"
                  style={{ backgroundColor: color.hex }}
                />
                <span className="text-xs text-slate-400">
                  {language === 'tr' ? color.nameTr : color.nameEn}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Score selection */}
        <div className="glass-panel p-6 space-y-4">
          <h3 className="text-amber-400 text-sm font-medium">
            {language === 'tr' ? 'Kaç tanesini doğru bildin?' : 'How many did you get right?'}
          </h3>
          <div className="flex flex-wrap gap-3 justify-center">
            {[0, 1, 2, 3, 4, 5].map((score) => (
              <button
                key={score}
                onClick={() => setSelectedScore(score)}
                className={`w-14 h-14 rounded-xl border-2 font-bold text-xl transition-all ${
                  selectedScore === score 
                    ? 'bg-violet-500 border-violet-400 text-white scale-110' 
                    : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-violet-500'
                }`}
              >
                {score}
              </button>
            ))}
          </div>
        </div>
        
        <button
          onClick={handleSubmit}
          disabled={selectedScore === null}
          className="w-full max-w-sm mx-auto rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 px-8 py-4 text-sm font-medium text-white transition-all hover:from-violet-500/30 hover:to-cyan-500/30 border border-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {language === 'tr' ? 'Sonucu Kaydet' : 'Save Result'}
        </button>
      </div>
    </motion.div>
  );
};

// Results Screen
const ResultsScreen = ({ language, score, totalRounds, shownColors, onRestart, onBack, onHome }) => {
  const percentage = Math.round((score / totalRounds) * 100);
  
  // Save to localStorage
  useEffect(() => {
    const history = JSON.parse(localStorage.getItem('zenermind_color_history') || '[]');
    history.push({
      date: new Date().toISOString(),
      score,
      total: totalRounds,
      percentage,
      colors: shownColors.map(c => c.id)
    });
    localStorage.setItem('zenermind_color_history', JSON.stringify(history.slice(-50)));
  }, [score, totalRounds, percentage, shownColors]);
  
  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      <HomeButton onHome={onHome} />
      
      <div className="max-w-2xl w-full space-y-8 text-center">
        <div className="space-y-4">
          <h1 className="text-3xl font-heading text-slate-100">
            {language === 'tr' ? 'Sonuçların' : 'Your Results'}
          </h1>
        </div>
        
        {/* Score display */}
        <div className="glass-panel p-8 space-y-4">
          <div className="text-6xl font-heading text-violet-400">
            {score} / {totalRounds}
          </div>
          <div className="text-2xl text-slate-300">
            %{percentage}
          </div>
          <p className="text-slate-500 text-sm">
            {percentage >= 80 && (language === 'tr' ? 'Mükemmel! Güçlü bir sezgisel algı.' : 'Excellent! Strong intuitive perception.')}
            {percentage >= 60 && percentage < 80 && (language === 'tr' ? 'Çok iyi! Gelişen bir yetenek.' : 'Very good! A developing ability.')}
            {percentage >= 40 && percentage < 60 && (language === 'tr' ? 'İyi! Pratiğe devam et.' : 'Good! Keep practicing.')}
            {percentage < 40 && (language === 'tr' ? 'Pratik yap, gelişeceksin.' : 'Practice more, you will improve.')}
          </p>
        </div>
        
        {/* Shown colors */}
        <div className="glass-panel p-6 space-y-4">
          <h3 className="text-cyan-400 text-sm font-medium text-left">
            {language === 'tr' ? 'Gösterilen Renkler' : 'Shown Colors'}
          </h3>
          <div className="flex flex-wrap gap-3 justify-center">
            {shownColors.map((color, index) => (
              <div key={`shown-${color.id}-${index}`} className="flex flex-col items-center gap-2">
                <div
                  className="w-14 h-14 rounded-xl border border-white/10 shadow-lg"
                  style={{ backgroundColor: color.hex }}
                />
                <span className="text-xs text-slate-400">
                  {language === 'tr' ? color.nameTr : color.nameEn}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex gap-4 justify-center">
          <button
            onClick={onRestart}
            className="rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 px-8 py-4 text-sm font-medium text-white transition-all hover:from-violet-500/30 hover:to-cyan-500/30 border border-violet-500/30"
          >
            {language === 'tr' ? 'Tekrar Dene' : 'Try Again'}
          </button>
          
          <button
            onClick={onHome}
            className="rounded-full bg-slate-800/50 px-8 py-4 text-sm font-medium text-slate-300 border border-slate-700/50 flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            Home
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Main Component
export const ColorTraining = ({ onBack, onHome }) => {
  const [phase, setPhase] = useState('intro'); // intro, instructions, test, score, results
  const [language, setLanguage] = useState('tr');
  const [currentRound, setCurrentRound] = useState(1);
  const [shownColors, setShownColors] = useState([]);
  const [score, setScore] = useState(0);
  const totalRounds = 5;
  
  const handleStart = () => {
    setPhase('instructions');
  };
  
  const handleInstructionsContinue = () => {
    setPhase('test');
    setCurrentRound(1);
    setShownColors([]);
  };
  
  const handleTestComplete = (finalColors, nextRound, newColors) => {
    if (finalColors) {
      // All rounds complete
      setShownColors(finalColors);
      setPhase('score');
    } else if (nextRound && newColors) {
      // Go to next round
      setCurrentRound(nextRound);
      setShownColors(newColors);
    }
  };
  
  const handleScoreSubmit = (selectedScore) => {
    setScore(selectedScore);
    setPhase('results');
  };
  
  const handleRestart = () => {
    setPhase('intro');
    setCurrentRound(1);
    setShownColors([]);
    setScore(0);
  };
  
  const handleExit = () => {
    setPhase('intro');
  };
  
  const handleHomeClick = () => {
    if (onHome) {
      onHome();
    } else if (onBack) {
      onBack();
    }
  };
  
  return (
    <AnimatePresence mode="wait">
      {phase === 'intro' && (
        <IntroScreen
          key="intro"
          language={language}
          onLanguageChange={setLanguage}
          onStart={handleStart}
          onBack={onBack}
          onHome={handleHomeClick}
        />
      )}
      
      {phase === 'instructions' && (
        <InstructionsScreen
          key="instructions"
          language={language}
          onContinue={handleInstructionsContinue}
          onBack={() => setPhase('intro')}
          onHome={handleHomeClick}
        />
      )}
      
      {phase === 'test' && (
        <TestPhase
          key={`test-${currentRound}`}
          language={language}
          round={currentRound}
          totalRounds={totalRounds}
          shownColors={shownColors}
          onComplete={handleTestComplete}
          onExit={handleExit}
          onHome={handleHomeClick}
        />
      )}
      
      {phase === 'score' && (
        <ScoreSelectionScreen
          key="score"
          language={language}
          totalRounds={totalRounds}
          shownColors={shownColors}
          onSubmit={handleScoreSubmit}
          onBack={handleExit}
          onHome={handleHomeClick}
        />
      )}
      
      {phase === 'results' && (
        <ResultsScreen
          key="results"
          language={language}
          score={score}
          totalRounds={totalRounds}
          shownColors={shownColors}
          onRestart={handleRestart}
          onBack={onBack}
          onHome={handleHomeClick}
        />
      )}
    </AnimatePresence>
  );
};

export default ColorTraining;
